import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  User,
  Search,
  X,
  CheckCircle,
  AlertCircle,
  Loader,
  ArrowLeft,
  Book,
  Printer,
  Camera,
  RotateCcw,
  Users,
  ChevronRight,
  Sparkles,
  Hash
} from 'lucide-react';
import api from '../api';
import BarcodeScanner from './components/BarcodeScanner';

const ReturnBook = () => {
  const navigate = useNavigate();
  const [, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [transaction, setTransaction] = useState(null);
  const [notes, setNotes] = useState('');
  const [returning, setReturning] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [rules, setRules] = useState(null);
  const [renewing, setRenewing] = useState(false);
  const [isBarcodeScanning, setIsBarcodeScanning] = useState(false);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('all');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const resultsRef = useRef(null);
  const scannerBufferRef = useRef('');
  const scannerTimeoutRef = useRef(null);
  const tenantId = localStorage.getItem('tenantId');

  const searchTypes = [
    { value: 'all', label: 'All Fields', icon: Search },
    { value: 'barcode', label: 'Book Barcode', icon: Book },
    { value: 'title', label: 'Book Title', icon: BookOpen },
    { value: 'author', label: 'Author', icon: User },
    { value: 'member', label: 'Member Name', icon: Users },
    { value: 'usn', label: 'USN / ID', icon: Hash }
  ];

  // ==================== PROCESS RETURN BOOK ====================
  const processReturnBook = useCallback(async (book, copy, activeTransaction) => {
    try {
      let member = null;
      if (activeTransaction.memberName) {
        member = {
          name: activeTransaction.memberName,
          email: activeTransaction.memberEmail || '',
          usn: activeTransaction.memberUSN || '',
          role: activeTransaction.memberType || 'student',
          id: activeTransaction.memberId
        };
      }

      let fineData = {};
      try {
        const fineResponse = await api.get(`/api/transactions/${activeTransaction._id}/fine`, {
          params: { tenantId }
        });
        fineData = fineResponse.data.data || {};
      } catch (err) {
        console.error('Error fetching fine details:', err);
        const now = new Date();
        const dueDate = new Date(activeTransaction.dueDate);
        if (now > dueDate) {
          const daysLate = Math.ceil((now - dueDate) / (1000 * 60 * 60 * 24));
          const gracePeriod = 2;
          const finePerDay = activeTransaction.finePerDay || 2;
          const effectiveDays = Math.max(0, daysLate - gracePeriod);
          fineData = {
            isOverdue: true,
            daysLate: effectiveDays,
            fineAmount: effectiveDays * finePerDay,
            perDay: finePerDay
          };
        } else {
          fineData = {
            isOverdue: false,
            daysLate: 0,
            fineAmount: 0,
            perDay: activeTransaction.finePerDay || 2
          };
        }
      }

      if (member?.role) {
        try {
          const rulesResponse = await api.get(`/api/library/rules/${tenantId}/role/${member.role}`);
          if (rulesResponse.data.status === 'success') {
            setRules(rulesResponse.data.data.rules);
          }
        } catch (err) {
          console.error('Error fetching rules:', err);
          setRules({
            maxBooksAllowed: member.role === 'student' ? 5 : 10,
            defaultIssueDays: member.role === 'student' ? 15 : 50,
            finePerDay: member.role === 'student' ? 2 : 0,
            maxFineCap: 500,
            gracePeriodDays: 2,
            maxRenewals: 2,
            renewalDays: 7
          });
        }
      }

      setTransaction({
        ...activeTransaction,
        book: book,
        copy: copy,
        member: member,
        fine: fineData,
        daysLate: fineData.daysLate || 0,
        fineAmount: fineData.fineAmount || 0,
        finePerDay: fineData.perDay || 2,
        isOverdue: fineData.isOverdue || false
      });

      setSuccess(`Record loaded: ${book.title} (Copy #${copy?.copyNumber || 1})`);
      setTimeout(() => setSuccess(null), 3000);
      setShowSearchResults(false);
      setSearchResults([]);
      setSearchQuery('');
    } catch (err) {
      throw err;
    }
  }, [tenantId]);

  // ==================== BARCODE SCAN HANDLER ====================
  const handleBarcodeScan = useCallback(async (barcodeValue) => {
    if (!barcodeValue || barcodeValue.trim() === '') {
      setError('Please scan a valid book copy barcode');
      setTimeout(() => setError(null), 3000);
      return;
    }

    setIsBarcodeScanning(true);
    setLoading(true);
    setError(null);
    setTransaction(null);
    setShowReceipt(false);

    try {
      const response = await api.get('/api/transactions/find-book', {
        params: { tenantId, identifier: barcodeValue }
      });

      if (!response.data.success) {
        setError('Book copy record not found');
        setTimeout(() => setError(null), 5000);
        setIsBarcodeScanning(false);
        setLoading(false);
        return;
      }

      const { book, copy, transaction: activeTransaction } = response.data.data;

      if (!activeTransaction) {
        setError('This book copy is currently in stock (Not issued)');
        setTimeout(() => setError(null), 5000);
        setIsBarcodeScanning(false);
        setLoading(false);
        return;
      }

      await processReturnBook(book, copy, activeTransaction);
      setShowScanner(false);
    } catch (err) {
      console.error('Error processing return:', err);
      setError(err.response?.data?.message || 'Failed to process book return');
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsBarcodeScanning(false);
      setLoading(false);
      setShowScanner(false);
    }
  }, [tenantId, processReturnBook]);

  // ==================== HARDWARE SCANNER SUPPORT ====================
  const handleScannerInput = useCallback((barcode) => {
    if (!barcode || barcode.trim() === '') return;
    const trimmedBarcode = barcode.trim();
    setSearchQuery(trimmedBarcode);
    setTimeout(() => {
      handleBarcodeScan(trimmedBarcode);
    }, 300);
  }, [handleBarcodeScan]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const target = event.target;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        return;
      }

      const key = event.key;

      if (key === 'Enter') {
        if (scannerBufferRef.current.trim()) {
          event.preventDefault();
          const barcode = scannerBufferRef.current.trim();
          console.log('📷 Hardware scanner detected in return:', barcode);
          handleScannerInput(barcode);
          scannerBufferRef.current = '';
        }
        return;
      }

      if (key.length === 1 && key.match(/[a-zA-Z0-9\-_./]/)) {
        if (scannerTimeoutRef.current) {
          clearTimeout(scannerTimeoutRef.current);
        }

        scannerBufferRef.current += key;

        scannerTimeoutRef.current = setTimeout(() => {
          if (scannerBufferRef.current.trim()) {
            const barcode = scannerBufferRef.current.trim();
            console.log('📷 Hardware scanner auto-detected in return:', barcode);
            handleScannerInput(barcode);
            scannerBufferRef.current = '';
          }
          scannerTimeoutRef.current = null;
        }, 150);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (scannerTimeoutRef.current) {
        clearTimeout(scannerTimeoutRef.current);
      }
    };
  }, [handleScannerInput]);

  useEffect(() => {
    if (inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (resultsRef.current && !resultsRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(searchQuery);
    }, 400);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, searchType]);

  // ==================== SEARCH FUNCTIONS ====================
  const performSearch = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      setIsSearching(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/api/transactions/search-issued', {
        params: { tenantId, query, searchType, limit: 20 }
      });

      if (response.data.success) {
        setSearchResults(response.data.data);
        setShowSearchResults(response.data.data.length > 0);
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Search failed. Please try again.');
      setTimeout(() => setError(null), 5000);
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  const handleSelectResult = useCallback(async (result) => {
    setLoading(true);
    try {
      const response = await api.get('/api/transactions/find-book', {
        params: { tenantId, identifier: result.bookBarcode || result.bookId }
      });

      if (response.data.success) {
        const { book, copy, transaction: activeTransaction } = response.data.data;
        if (activeTransaction) {
          await processReturnBook(book, copy, activeTransaction);
        } else {
          setError('This book copy is currently not marked as issued');
          setTimeout(() => setError(null), 5000);
        }
      }
    } catch (err) {
      console.error('Error selecting result:', err);
      if (result) {
        try {
          const book = {
            title: result.bookTitle,
            author: result.bookAuthor,
            masterBarcode: result.bookBarcode,
            isbn: result.bookISBN,
            _id: result.bookId
          };
          
          const copy = {
            copyNumber: result.copyNumber || 1,
            barcode: result.bookBarcode
          };
          
          const activeTransaction = {
            _id: result.id,
            memberId: result.memberId,
            memberName: result.memberName,
            memberEmail: result.memberEmail,
            memberUSN: result.memberUSN,
            memberType: result.memberRole || 'student',
            issueDate: result.issueDate,
            dueDate: result.dueDate,
            status: result.status || 'issued',
            renewalCount: result.renewalCount || 0,
            finePerDay: result.finePerDay || 2,
            gracePeriodDays: result.gracePeriodDays || 2,
            maxFineCap: result.maxFineCap || 500,
            maxRenewals: result.maxRenewals || 2,
            bookBarcode: result.bookBarcode
          };
          
          await processReturnBook(book, copy, activeTransaction);
        } catch (fallbackErr) {
          setError('Failed to load transaction details.');
          setTimeout(() => setError(null), 5000);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [processReturnBook, tenantId]);

  // ==================== RETURN HANDLERS ====================
  const handleReturn = async () => {
    if (!transaction) {
      setError('No transaction loaded to return');
      return;
    }

    setReturning(true);
    setError(null);

    try {
      const response = await api.post('/api/transactions/return', {
        tenantId,
        transactionId: transaction._id,
        notes: notes,
        barcode: transaction.bookBarcode
      });

      if (response.data.success) {
        const fine = response.data.fineAmount || 0;
        if (fine > 0) {
          setSuccess(`✅ Book returned successfully! Overdue fine applied: ₹${fine}`);
        } else {
          setSuccess('✅ Book returned successfully! Returned on time.');
        }
        setShowReceipt(true);
        
        setTransaction({
          ...transaction,
          returned: true,
          actualFine: fine
        });

        setTimeout(() => {
          setTransaction(null);
          setShowReceipt(false);
          setSearchResults([]);
          setShowSearchResults(false);
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 6000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to complete book return');
      setTimeout(() => setError(null), 5000);
    } finally {
      setReturning(false);
    }
  };

  const handleRenewBook = async () => {
    if (!transaction) {
      setError('No transaction loaded to extend');
      return;
    }

    if (transaction.isOverdue) {
      setError('Overdue books cannot be renewed. Please complete return and clear fine.');
      setTimeout(() => setError(null), 5000);
      return;
    }

    const maxRenewals = rules?.maxRenewals || 2;
    if (transaction.renewalCount >= maxRenewals) {
      setError(`Maximum extension limit (${maxRenewals} times) reached for this item.`);
      setTimeout(() => setError(null), 5000);
      return;
    }

    const days = window.prompt(`Enter renewal days extension (Default: ${rules?.renewalDays || 7} days):`, rules?.renewalDays || 7);
    if (days === null) return;

    const renewalDaysCount = parseInt(days) || rules?.renewalDays || 7;
    if (renewalDaysCount <= 0) {
      setError('Please enter a valid number of days');
      setTimeout(() => setError(null), 5000);
      return;
    }

    setRenewing(true);
    setError(null);

    try {
      const response = await api.post('/api/transactions/renew', {
        tenantId,
        transactionId: transaction._id,
        renewalDays: renewalDaysCount,
        notes: `Renewed at desk for ${renewalDaysCount} days. ${notes || ''}`
      });

      if (response.data.success) {
        const newDueDate = new Date(response.data.data.newDueDate);
        setSuccess(`✅ Extended successfully! Extended return due date: ${newDueDate.toLocaleDateString('en-IN')}`);
        setTransaction({
          ...transaction,
          dueDate: response.data.data.newDueDate,
          renewalCount: response.data.data.renewalCount,
          remainingRenewals: response.data.data.remainingRenewals
        });
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Could not renew book');
      setTimeout(() => setError(null), 5000);
    } finally {
      setRenewing(false);
    }
  };

  const openScanner = () => {
    setShowScanner(true);
  };

  const handlePrintReceipt = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please enable browser popups to generate print receipts');
      return;
    }

    const content = `
      <html>
        <head>
          <title>Library Return Receipt</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 30px; max-width: 500px; margin: 0 auto; color: #1e293b; }
            .header { text-align: center; border-bottom: 2px solid #e2e8f0; padding-bottom: 15px; margin-bottom: 20px; }
            .header h1 { margin: 0; font-size: 20px; color: #0f172a; }
            .row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f1f5f9; font-size: 14px; }
            .label { font-weight: 600; color: #64748b; }
            .fine { font-size: 16px; font-weight: bold; }
            .fine-positive { color: #dc2626; }
            .fine-zero { color: #16a34a; }
            .footer { text-align: center; margin-top: 25px; padding-top: 15px; border-top: 2px solid #e2e8f0; color: #94a3b8; font-size: 11px; }
            .barcode-tag { font-family: monospace; background: #f1f5f9; padding: 2px 6px; border-radius: 4px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>📚 Library Return Desk Receipt</h1>
            <p style="margin: 4px 0 0 0; font-size: 12px; color: #64748b;">${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</p>
            <p style="margin: 2px 0 0 0; font-size: 11px; font-family: monospace;">Ref: #${transaction._id.slice(-8).toUpperCase()}</p>
          </div>
          <div>
            <div class="row"><span class="label">Book Title</span><span style="font-weight:600;">${transaction.book?.title || 'Unknown'}</span></div>
            <div class="row"><span class="label">Author</span><span>${transaction.book?.author || 'Unknown'}</span></div>
            <div class="row"><span class="label">Copy Barcode</span><span class="barcode-tag">${transaction.bookBarcode}</span></div>
            <div class="row"><span class="label">Borrower</span><span>${transaction.member?.name || 'Unknown'}</span></div>
            <div class="row"><span class="label">USN / ID</span><span>${transaction.member?.usn || 'N/A'}</span></div>
            <div class="row"><span class="label">Issued On</span><span>${new Date(transaction.issueDate).toLocaleDateString()}</span></div>
            <div class="row"><span class="label">Due Date</span><span>${new Date(transaction.dueDate).toLocaleDateString()}</span></div>
            <div class="row"><span class="label">Returned On</span><span>${new Date().toLocaleDateString()}</span></div>
            
            ${transaction.actualFine > 0 ? `
              <div class="row" style="background: #fef2f2; margin-top: 10px; padding: 12px; border-radius: 8px;">
                <span class="label" style="color: #991b1b;">Overdue Fine Paid</span>
                <span class="fine fine-positive">₹${transaction.actualFine}</span>
              </div>
            ` : `
              <div class="row" style="background: #f0fdf4; margin-top: 10px; padding: 12px; border-radius: 8px;">
                <span class="label" style="color: #166534;">Fine Status</span>
                <span class="fine fine-zero">No Fine (On Time)</span>
              </div>
            `}
          </div>
          <div class="footer"><p>Thank you for returning the library material in good condition.</p></div>
          <script>window.onload = function() { setTimeout(function() { window.print(); window.close(); }, 400); }</script>
        </body>
      </html>
    `;

    printWindow.document.write(content);
    printWindow.document.close();
  };

  const resetForm = () => {
    setTransaction(null);
    setNotes('');
    setShowReceipt(false);
    setError(null);
    setSuccess(null);
    setRules(null);
    setSearchResults([]);
    setShowSearchResults(false);
    setSearchQuery('');
    if (inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  };

  const canRenew = () => {
    if (!transaction) return false;
    if (transaction.isOverdue) return false;
    const maxRenewals = rules?.maxRenewals || 2;
    const renewalCount = transaction.renewalCount || 0;
    return renewalCount < maxRenewals;
  };

  const getRemainingRenewals = () => {
    if (!transaction) return 0;
    const maxRenewals = rules?.maxRenewals || 2;
    const renewalCount = transaction.renewalCount || 0;
    return Math.max(0, maxRenewals - renewalCount);
  };

  const highlightMatch = (text, query) => {
    if (!query || !text || typeof text !== 'string') return text || '';
    const index = text.toLowerCase().indexOf(query.toLowerCase());
    if (index === -1) return text;
    return (
      <>
        {text.substring(0, index)}
        <span className="bg-amber-200 dark:bg-amber-800/60 text-slate-900 dark:text-white font-semibold">
          {text.substring(index, index + query.length)}
        </span>
        {text.substring(index + query.length)}
      </>
    );
  };

  return (
    <div className="space-y-5 sm:space-y-6 max-w-4xl mx-auto">
      
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b border-slate-200 dark:border-gray-700">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white flex items-center">
            <RotateCcw className="h-5 w-5 mr-2 text-emerald-600 dark:text-emerald-400" />
            Book Return Counter
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">Scan book barcode or search active issued records to accept returns</p>
        </div>

        <button 
          onClick={() => navigate('/librarian/transactions')} 
          className="self-start sm:self-auto px-3 py-2 border border-slate-300 dark:border-gray-600 hover:bg-slate-100 dark:hover:bg-gray-700 text-slate-700 dark:text-slate-300 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center space-x-1.5"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>All Records</span>
        </button>
      </div>

      {/* Dynamic Alerts */}
      {success && (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4 text-emerald-800 dark:text-emerald-300 flex items-center justify-between shadow-sm animate-fadeIn">
          <span className="flex items-center text-xs sm:text-sm font-medium">
            <CheckCircle className="h-5 w-5 mr-2.5 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
            {success}
          </span>
          <button onClick={() => setSuccess(null)} className="text-emerald-700 dark:text-emerald-300 hover:opacity-75 p-1">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {error && (
        <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-xl p-4 text-rose-800 dark:text-rose-300 flex items-center justify-between shadow-sm animate-fadeIn">
          <span className="flex items-center text-xs sm:text-sm font-medium">
            <AlertCircle className="h-5 w-5 mr-2.5 flex-shrink-0 text-rose-600 dark:text-rose-400" />
            {error}
          </span>
          <button onClick={() => setError(null)} className="text-rose-700 dark:text-rose-300 hover:opacity-75 p-1">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* SEARCH/SCAN SECTION */}
      {!transaction && !showReceipt && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-4 sm:p-6 space-y-4 shadow-sm">
          
          <button 
            onClick={openScanner} 
            disabled={isBarcodeScanning}
            className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 disabled:opacity-50 text-white rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 shadow-md shadow-emerald-500/20 text-sm"
          >
            {isBarcodeScanning ? (
              <Loader className="h-5 w-5 animate-spin" />
            ) : (
              <Camera className="h-5 w-5" />
            )}
            <span>{isBarcodeScanning ? 'Reading Scanner...' : 'Scan Book Copy Barcode'}</span>
          </button>

          <div className="relative flex items-center justify-center">
            <div className="w-full border-t border-slate-200 dark:border-gray-700"></div>
            <span className="absolute bg-white dark:bg-gray-800 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">or search active records</span>
          </div>

          {/* Filter Pills */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
            {searchTypes.map((type) => {
              const Icon = type.icon;
              const isSelected = searchType === type.value;
              return (
                <button
                  key={type.value}
                  onClick={() => setSearchType(type.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center space-x-1.5 ${
                    isSelected
                      ? 'bg-emerald-600 text-white shadow-sm'
                      : 'bg-slate-100 text-slate-600 dark:bg-gray-700 dark:text-slate-300 hover:bg-slate-200'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span>{type.label}</span>
                </button>
              );
            })}
          </div>

          {/* Input Search Box */}
          <div className="relative" ref={resultsRef}>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
              Type Search Term
              {isSearching && <span className="ml-2 text-xs text-emerald-500 animate-pulse font-normal">(Searching...)</span>}
            </label>

            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search issued books by title, barcode, student USN..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-10 py-3 rounded-xl border border-slate-300 dark:border-gray-600 bg-slate-50 dark:bg-gray-700/50 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSearchResults([]);
                    setShowSearchResults(false);
                    if (inputRef.current) inputRef.current.focus();
                  }}
                  className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Dropdown Results */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="absolute z-50 left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 shadow-2xl max-h-72 overflow-y-auto divide-y divide-slate-100 dark:divide-gray-700">
                <div className="sticky top-0 p-2.5 bg-slate-50 dark:bg-gray-700/90 backdrop-blur-sm border-b border-slate-100 dark:border-gray-700 z-10 flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                    {searchResults.length} Issued Book(s) Found
                  </span>
                </div>

                {searchResults.map((result, index) => (
                  <button
                    key={index}
                    onClick={() => handleSelectResult(result)}
                    className="w-full text-left px-4 py-3 hover:bg-emerald-50/60 dark:hover:bg-gray-700/70 transition-colors flex items-center justify-between group"
                  >
                    <div className="flex items-start space-x-3 min-w-0 pr-2">
                      <div className="p-2 bg-purple-100 dark:bg-purple-900/30 text-purple-600 rounded-lg flex-shrink-0 mt-0.5">
                        <Book className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {highlightMatch(result.bookTitle, searchQuery)}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">
                          Borrower: <strong className="text-slate-800 dark:text-slate-200">{highlightMatch(result.memberName, searchQuery)}</strong>
                        </p>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400 mt-1">
                          <span className="font-mono">Barcode: {highlightMatch(result.bookBarcode, searchQuery)}</span>
                          <span>• Due: {new Date(result.dueDate).toLocaleDateString('en-IN')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${
                        result.isOverdue 
                          ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300' 
                          : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                      }`}>
                        {result.isOverdue ? 'Overdue' : 'Active Issue'}
                      </span>
                      <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-emerald-600" />
                    </div>
                  </button>
                ))}
              </div>
            )}

            {showSearchResults && searchResults.length === 0 && searchQuery.length >= 2 && (
              <div className="absolute z-50 left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-4 shadow-xl text-center">
                <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">No active issued book found for "{searchQuery}"</p>
                <p className="text-xs text-slate-400 mt-0.5">Verify if the book is already returned or scan barcode again.</p>
              </div>
            )}
          </div>

          <div className="p-3 bg-slate-50 dark:bg-gray-700/30 rounded-xl border border-slate-200 dark:border-gray-700 text-xs text-slate-600 dark:text-slate-300 space-y-1">
            <p className="font-semibold text-slate-900 dark:text-white flex items-center">
              <Sparkles className="h-3.5 w-3.5 mr-1 text-emerald-600" /> Quick Instructions:
            </p>
            <p>• Scanning a copy barcode will instantly load the return summary.</p>
            <p>• Overdue fines are automatically calculated based on tenant rules.</p>
          </div>
        </div>
      )}

      {/* TRANSACTION VERIFICATION AND RETURN ACTION */}
      {transaction && !showReceipt && (
        <div className="space-y-4">
          
          {/* Main Book & Borrower Summary Card */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-4 sm:p-6 space-y-4 shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-gray-700 pb-3">
              <h3 className="font-bold text-gray-900 dark:text-white text-base flex items-center">
                <Book className="h-5 w-5 mr-2 text-blue-600" /> Verify Book Return
              </h3>
              
              <button 
                onClick={resetForm} 
                className="text-xs font-semibold text-slate-500 hover:text-slate-700 dark:text-slate-400 p-1"
              >
                Clear Selection
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Book Info */}
              <div className="p-4 bg-slate-50 dark:bg-gray-700/30 rounded-xl border border-slate-200 dark:border-gray-700 space-y-1.5">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Book Details</p>
                <p className="font-bold text-gray-900 dark:text-white text-base">{transaction.book?.title || 'Unknown Title'}</p>
                <p className="text-xs text-slate-600 dark:text-slate-300">by {transaction.book?.author || 'Unknown Author'}</p>
                <p className="text-xs font-mono text-slate-500 pt-1">
                  Copy Barcode: <strong>{transaction.bookBarcode}</strong> (Copy #{transaction.copy?.copyNumber || 1})
                </p>
              </div>

              {/* Borrower Info */}
              <div className="p-4 bg-slate-50 dark:bg-gray-700/30 rounded-xl border border-slate-200 dark:border-gray-700 space-y-1.5">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Borrower Details</p>
                <p className="font-bold text-gray-900 dark:text-white text-base">{transaction.member?.name || 'Unknown'}</p>
                <p className="text-xs text-slate-600 dark:text-slate-300">USN / ID: {transaction.member?.usn || 'N/A'}</p>
                <p className="text-xs text-slate-500 truncate">{transaction.member?.email}</p>
              </div>
            </div>

            {/* Timeline & Fine Status */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
              <div className="p-3 bg-slate-50 dark:bg-gray-700/20 rounded-xl border border-slate-200 dark:border-gray-700">
                <p className="text-xs text-slate-400 font-medium">Issue Date</p>
                <p className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white mt-0.5">
                  {new Date(transaction.issueDate).toLocaleDateString('en-IN')}
                </p>
              </div>

              <div className="p-3 bg-slate-50 dark:bg-gray-700/20 rounded-xl border border-slate-200 dark:border-gray-700">
                <p className="text-xs text-slate-400 font-medium">Due Date</p>
                <p className={`text-xs sm:text-sm font-bold mt-0.5 ${transaction.isOverdue ? 'text-rose-600 dark:text-rose-400' : 'text-gray-900 dark:text-white'}`}>
                  {new Date(transaction.dueDate).toLocaleDateString('en-IN')}
                </p>
              </div>

              <div className="p-3 bg-slate-50 dark:bg-gray-700/20 rounded-xl border border-slate-200 dark:border-gray-700">
                <p className="text-xs text-slate-400 font-medium">Status</p>
                <p className={`text-xs sm:text-sm font-bold mt-0.5 ${transaction.isOverdue ? 'text-rose-600' : 'text-emerald-600'}`}>
                  {transaction.isOverdue ? `${transaction.daysLate} Days Overdue` : 'On Time'}
                </p>
              </div>

              <div className={`p-3 rounded-xl border ${transaction.isOverdue ? 'bg-rose-50 dark:bg-rose-900/20 border-rose-200' : 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200'}`}>
                <p className="text-xs font-medium text-slate-500">Calculated Fine</p>
                <p className={`text-sm sm:text-base font-black mt-0.5 ${transaction.isOverdue ? 'text-rose-600' : 'text-emerald-600'}`}>
                  ₹{transaction.fineAmount || 0}
                </p>
              </div>
            </div>

            {/* Optional Notes */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1">
                Return Condition Notes
              </label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Good condition, returned at front counter..."
                className="w-full px-3 py-2 rounded-xl border border-slate-300 dark:border-gray-600 bg-slate-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            {/* Extensions Panel */}
            {canRenew() && (
              <div className="p-3 bg-blue-50/60 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800/60 flex items-center justify-between gap-3">
                <div className="text-xs text-blue-900 dark:text-blue-300">
                  <p className="font-bold">Eligible for Renewal Extension</p>
                  <p>Remaining allowed extensions: {getRemainingRenewals()}</p>
                </div>
                <button
                  onClick={handleRenewBook}
                  disabled={renewing}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition-colors flex items-center space-x-1.5"
                >
                  {renewing ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                  <span>Renew Book</span>
                </button>
              </div>
            )}

            {/* Final Action Buttons */}
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-3 border-t border-slate-100 dark:border-gray-700">
              <button
                onClick={resetForm}
                className="px-4 py-2.5 border border-slate-300 dark:border-gray-600 hover:bg-slate-100 dark:hover:bg-gray-700 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-semibold transition-colors"
              >
                Cancel
              </button>
              
              <button
                onClick={handleReturn}
                disabled={returning}
                className={`flex-1 px-4 py-2.5 text-white rounded-xl text-sm font-semibold transition-all flex items-center justify-center space-x-2 shadow-md ${
                  transaction.isOverdue 
                    ? 'bg-rose-600 hover:bg-rose-700 shadow-rose-500/20' 
                    : 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-500/20'
                }`}
              >
                {returning ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    <span>Processing Return...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    <span>Confirm Book Return</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RECEIPT VIEW */}
      {showReceipt && transaction && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-6 space-y-5 shadow-sm text-center">
          <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto">
            <CheckCircle className="h-7 w-7" />
          </div>

          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Book Return Completed</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Copy barcode {transaction.bookBarcode} has been checked back into the library catalog
            </p>
          </div>

          <div className="bg-slate-50 dark:bg-gray-700/30 p-4 rounded-xl border border-slate-200 dark:border-gray-700 text-left space-y-2 text-xs sm:text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Book Title:</span>
              <span className="font-semibold text-gray-900 dark:text-white">{transaction.book?.title}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Borrower:</span>
              <span className="font-semibold text-gray-900 dark:text-white">{transaction.member?.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Return Date:</span>
              <span className="font-semibold text-gray-900 dark:text-white">{new Date().toLocaleDateString('en-IN')}</span>
            </div>
            <div className="flex justify-between border-t border-slate-200 dark:border-gray-600 pt-2">
              <span className="text-slate-500 font-semibold">Fine Collected:</span>
              <span className={`font-bold ${transaction.actualFine > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                ₹{transaction.actualFine || 0}
              </span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              onClick={handlePrintReceipt}
              className="flex-1 px-4 py-2.5 border border-slate-300 dark:border-gray-600 hover:bg-slate-100 text-slate-700 dark:text-slate-200 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center space-x-2"
            >
              <Printer className="h-4 w-4" />
              <span>Print Slip</span>
            </button>
            
            <button
              onClick={resetForm}
              className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold transition-all flex items-center justify-center space-x-2 shadow-md shadow-blue-500/20"
            >
              <RotateCcw className="h-4 w-4" />
              <span>Next Return</span>
            </button>
          </div>
        </div>
      )}

      {/* Barcode Scanner Modal */}
      {showScanner && (
        <BarcodeScanner
          onScan={handleBarcodeScan}
          onClose={() => setShowScanner(false)}
          title="Scan Book Barcode for Return"
          description="Use camera lens or hardware scanner to read the book copy barcode"
          enableHardware={true}
          enableCamera={true}
        />
      )}
    </div>
  );
};

export default ReturnBook;